/**
 * AI Feedback Collection Hooks
 *
 * React Query hooks for submitting and retrieving feedback on AI outputs.
 * Users can rate outputs as helpful/not helpful or flag for review.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { functions } from '../../config/firebase';

// =============================================================================
// TYPES
// =============================================================================

export type FeedbackType = 'helpful' | 'not_helpful' | 'flagged';

export interface AIFeedback {
  id: string;
  aiOutputId: string;
  aiType: string;
  system: string;
  sourceId: string;
  sourceType: string;
  output: string;
  feedbackType: FeedbackType;
  flaggedBy: string;
  flaggedAt: Date;
  reason?: string;
  status: 'pending_review' | 'reviewed' | 'resolved';
  reviewedBy?: string;
  reviewedAt?: Date;
  resolution?: string;
}

export interface FeedbackStats {
  totalOutputs: number;
  totalFeedback: number;
  helpfulCount: number;
  notHelpfulCount: number;
  flaggedCount: number;
  pendingReviewCount: number;
  helpfulRate: number;
}

export interface UserFeedbackResult {
  hasFeedback: boolean;
  feedbackType?: FeedbackType;
  feedbackId?: string;
}

// =============================================================================
// QUERY KEY FACTORY
// =============================================================================

export const feedbackKeys = {
  all: ['ai-feedback'] as const,
  stats: () => [...feedbackKeys.all, 'stats'] as const,
  statsByFilter: (filters?: { system?: string; type?: string }) =>
    [...feedbackKeys.stats(), filters] as const,
  userFeedback: () => [...feedbackKeys.all, 'user'] as const,
  userFeedbackForOutput: (aiOutputId: string) =>
    [...feedbackKeys.userFeedback(), aiOutputId] as const,
};

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Submit feedback for an AI output
 *
 * @example
 * ```tsx
 * const submitFeedback = useSubmitAIFeedback();
 *
 * // Mark as helpful
 * await submitFeedback.mutateAsync({
 *   aiOutputId: 'output-123',
 *   feedbackType: 'helpful'
 * });
 *
 * // Flag for review
 * await submitFeedback.mutateAsync({
 *   aiOutputId: 'output-123',
 *   feedbackType: 'flagged',
 *   reason: 'Incorrect extraction of date field'
 * });
 * ```
 */
export function useSubmitAIFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      aiOutputId: string;
      feedbackType: FeedbackType;
      reason?: string;
    }): Promise<{ success: boolean; feedbackId: string }> => {
      const fn = httpsCallable<
        typeof input,
        { success: boolean; feedbackId: string }
      >(functions, 'submitAIFeedback');

      const result = await fn(input);
      return result.data;
    },

    // Optimistic update for immediate UI feedback
    onMutate: async (variables) => {
      // Cancel in-flight queries
      await queryClient.cancelQueries({
        queryKey: feedbackKeys.userFeedbackForOutput(variables.aiOutputId),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<UserFeedbackResult>(
        feedbackKeys.userFeedbackForOutput(variables.aiOutputId)
      );

      // Optimistically update
      queryClient.setQueryData<UserFeedbackResult>(
        feedbackKeys.userFeedbackForOutput(variables.aiOutputId),
        {
          hasFeedback: true,
          feedbackType: variables.feedbackType,
          feedbackId: 'optimistic-' + Date.now(),
        }
      );

      return { previous };
    },

    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(
          feedbackKeys.userFeedbackForOutput(variables.aiOutputId),
          context.previous
        );
      }
    },

    onSettled: (_, __, variables) => {
      // Always revalidate
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.userFeedbackForOutput(variables.aiOutputId),
      });
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.stats(),
      });
    },
  });
}

/**
 * Get current user's feedback for a specific AI output
 *
 * @example
 * ```tsx
 * const { data } = useUserAIFeedback('output-123');
 *
 * if (data?.hasFeedback) {
 *   console.log('User rated this as:', data.feedbackType);
 * }
 * ```
 */
export function useUserAIFeedback(aiOutputId: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.userFeedbackForOutput(aiOutputId || ''),
    queryFn: async (): Promise<UserFeedbackResult> => {
      if (!aiOutputId) return { hasFeedback: false };

      const fn = httpsCallable<
        { aiOutputId: string },
        UserFeedbackResult
      >(functions, 'getUserAIFeedback');

      const result = await fn({ aiOutputId });
      return result.data;
    },
    enabled: !!aiOutputId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get feedback statistics
 *
 * @example
 * ```tsx
 * // Get all stats
 * const { data: stats } = useAIFeedbackStats();
 *
 * // Get stats for specific system/type
 * const { data: techStats } = useAIFeedbackStats({
 *   system: 'technician',
 *   type: 'extraction'
 * });
 * ```
 */
export function useAIFeedbackStats(filters?: {
  system?: string;
  type?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: feedbackKeys.statsByFilter(filters),
    queryFn: async (): Promise<FeedbackStats> => {
      const fn = httpsCallable<
        typeof filters,
        FeedbackStats
      >(functions, 'getAIFeedbackStats');

      const result = await fn(filters);
      return result.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
